import { Link } from "react-router";
import { api } from "../../lib/api";
import { useClientContext } from "./ClientWorkspace";
import type { ClientConnection } from "./client-model";
import { vendorLabel } from "../../lib/friendly-labels";
import { useConfirm } from "../../components/primitives/Confirm";
import { Table, type Column } from "../../components/primitives/Table";
import { Badge } from "../../components/primitives/Badge";
import { EmptyState } from "../../components/primitives/EmptyState";
import { envOptions } from "./client-model";

/**
 * Connections tab (spec §3.3): the apps this client is linked to.
 *
 * Moving a connection between environments changes which pipelines may use
 * it, which is a routing change and therefore Tier 1 (spec §7).
 */
export function ClientConnections() {
  const { connections, environments, refetchInstances } = useClientContext();
  const confirm = useConfirm();

  /** Move a connection to another environment, spelling out which pipelines stop
   *  matching events as a result. */
  async function retag(c: ClientConnection, next: string) {
    const label = next || "any environment";
    await confirm({
      tier: 1,
      title: `Move ${c.name} to ${label}?`,
      body: next
        ? `Only pipelines in ${next} will be able to use this connection. Pipelines in other environments that reference it will stop matching events.`
        : "Pipelines in every environment will be able to use this connection.",
      confirmLabel: "Change environment",
      onConfirm: async () => {
        await api.updateConnectorInstance(c.id, { environment: next || null });
        await refetchInstances();
      },
    });
    // On cancel the controlled <select> simply re-renders with the old value.
  }

  const columns: Column<ClientConnection>[] = [
    {
      key: "name",
      label: "CONNECTION",
      render: (c) => (
        <div>
          <Link to={`/connections/${c.id}`}>{vendorLabel(c.connectorType)}</Link>
          <div className="run-sub mono">{c.name}</div>
        </div>
      ),
    },
    {
      key: "env",
      label: "ENVIRONMENT",
      render: (c) => (
        <select
          className="field__input"
          aria-label={`Environment for ${c.name}`}
          value={c.environment ?? ""}
          onChange={(e) => void retag(c, e.target.value)}
        >
          {envOptions(environments, c.environment, "Any environment").map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ),
    },
    {
      key: "scope",
      label: "SCOPE",
      render: (c) => (
        <Badge tone={c.environment == null ? "muted" : "ok"}>{c.environment == null ? "Shared" : "Scoped"}</Badge>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      rows={connections}
      rowKey={(c) => c.id}
      empty={<EmptyState title="No connections for this client yet." />}
    />
  );
}
